/**
 * Pure sidebar utilities — conversation ordering, container display naming, and
 * context-menu position clamping. Extracted verbatim from layout/Sidebar.tsx
 * (behavior-preserving).
 */

import type { Conversation } from '@/types/conversations';
import { isConversationPinned } from '@/types/conversations';
import type { ContainerAccess } from '@/types/session';

export type ConversationBucket = 'pinned' | 'unpinned';

export function orderConversationsWithPinnedFirst(conversations: Conversation[]): Conversation[] {
  const pinned: Conversation[] = [];
  const unpinned: Conversation[] = [];

  for (const conversation of conversations) {
    if (isConversationPinned(conversation)) {
      pinned.push(conversation);
      continue;
    }
    unpinned.push(conversation);
  }

  return [...pinned, ...unpinned];
}

export function buildPinnedConversationOrder(
  conversations: Conversation[],
  options?: {
    targetConversationId?: string;
    placeTargetAtTopOf?: ConversationBucket;
  },
): string[] {
  const pinned: Conversation[] = [];
  const unpinned: Conversation[] = [];

  for (const conversation of conversations) {
    if (!conversation?.id) continue;
    if (isConversationPinned(conversation)) {
      pinned.push(conversation);
      continue;
    }
    unpinned.push(conversation);
  }

  const targetConversationId = options?.targetConversationId;
  const placeTargetAtTopOf = options?.placeTargetAtTopOf;
  if (targetConversationId && placeTargetAtTopOf) {
    const bucket = placeTargetAtTopOf === 'pinned' ? pinned : unpinned;
    const targetIndex = bucket.findIndex((conversation) => conversation.id === targetConversationId);
    if (targetIndex > 0) {
      const [targetConversation] = bucket.splice(targetIndex, 1);
      bucket.unshift(targetConversation);
    }
  }

  return [...pinned, ...unpinned].map((conversation) => conversation.id);
}

// Get display name for a container
export function getContainerDisplayName(container: ContainerAccess): string {
  // Use containerName if set (e.g., "Club Fred")
  if (container.containerName) {
    return container.containerName;
  }
  // Fallback for owner's workspace
  if (container.isOwner) {
    return 'My Workspace';
  }
  // Fallback for shared workspaces
  return container.ownerName || container.ownerEmail?.split('@')[0] || 'Shared Space';
}

/**
 * Clamp a context-menu position so the menu stays fully on screen.
 *
 * Mirrors the inline clamp formula used at the four context-menu sites
 * (conversation context-menu / actions-click 190x196, module context-menu /
 * actions-click 190x124). The mobile module long-press site uses a different
 * center-of-screen formula and is intentionally NOT routed through this util.
 */
export function clampMenuPosition({
  x,
  y,
  width,
  height,
  padding,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
  padding: number;
}): { x: number; y: number } {
  const clampedX = Math.max(
    padding,
    Math.min(x, window.innerWidth - width - padding),
  );
  const clampedY = Math.max(
    padding,
    Math.min(y, window.innerHeight - height - padding),
  );
  return { x: clampedX, y: clampedY };
}
